// Hook: useCampaignForm
// Purpose: Centralizes create-campaign form state (fields, theme, image,
// questions, navigation). Provides handlers for inputs, image compression,
// question management, and step navigation.
import { useState } from 'react';
import { useFormCache } from '../../../hooks/useFormCache';

// Constants for image compression
const MAX_IMAGE_DIMENSION = 350; // Max width or height in pixels (reduced from 400)
const INITIAL_QUALITY = 0.7; // Initial compression quality (reduced from 0.8)
const MIN_QUALITY = 0.4; // Minimum compression quality (reduced from 0.5)
const TARGET_SIZE_MB = 0.2; // Target size in MB (reduced from 0.5 to 200KB)
const LARGE_IMAGE_THRESHOLD_MB = 2; // Threshold to apply more aggressive compression

export const useCampaignForm = () => {
  const initialFormData = {
    name: '',
    title: '',
    description: '',
    businessName: '',
    website: '',
    email: '',
    phone: ''
  };
  
  // Form state
  const [formData, updateFormData, clearFormCache] = useFormCache(initialFormData);
  const [surveyQuestions, setSurveyQuestions] = useState([]);
  const [aiGeneratedFields, setAiGeneratedFields] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const [hasExplainerVideo, setHasExplainerVideo] = useState(false);
  const [explainerVideo, setExplainerVideo] = useState(null);
  
  // Theme state
  const [selectedTheme, setSelectedTheme] = useState('sunset');
  const [gradientColors, setGradientColors] = useState({
    from: '#1a365d',
    via: '#3182ce',
    to: '#2c5282'
  });
  const [gradientDirection, setGradientDirection] = useState('br'); // br = bottom-right
  const [hexText, setHexText] = useState('#ffffff');
  
  // Step navigation
  const [currentStep, setCurrentStep] = useState(0);
  const [showPreview, setShowPreview] = useState(true);
  
  // Input handlers
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    
    // When user edits a field, remove it from AI generated fields
    if (aiGeneratedFields[id]) {
      setAiGeneratedFields(prev => ({
        ...prev,
        [id]: false
      }));
    }
    
    updateFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleAddQuestion = () => {
    // Limit to a maximum of 3 questions
    if (surveyQuestions.length >= 3) {
      return;
    }
    
    // Add new question
    const newQuestion = { id: surveyQuestions.length + 1, question: '' };
    setSurveyQuestions([
      ...surveyQuestions,
      newQuestion
    ]);
    
    // Ensure this new question isn't marked as AI generated
    const newQuestionIndex = surveyQuestions.length;
    const fieldName = `question_${newQuestionIndex}`;
    
    // If aiGeneratedFields exists and has this field set, explicitly mark it as false
    if (aiGeneratedFields && Object.keys(aiGeneratedFields).includes(fieldName)) {
      setAiGeneratedFields(prev => ({
        ...prev,
        [fieldName]: false
      }));
    }
  };

  const handleRemoveQuestion = (id) => {
    setSurveyQuestions(surveyQuestions.filter(q => q.id !== id));
  };

  const handleQuestionChange = (id, value) => {
    // Check if this question was AI generated
    const questionIndex = surveyQuestions.findIndex(q => q.id === id);
    const fieldName = `question_${questionIndex}`;
    
    // When user edits a question, remove it from AI generated fields
    if (aiGeneratedFields[fieldName]) {
      setAiGeneratedFields(prev => ({
        ...prev,
        [fieldName]: false
      }));
    }
    
    // Ensure value is not null
    const safeValue = value === null ? '' : value;
    
    // Update the survey question
    const updatedQuestions = surveyQuestions.map(q => 
      q.id === id ? { ...q, question: safeValue } : q
    );
    
    console.log('Updating survey question:', {
      id,
      value: safeValue,
      hasContent: Boolean(safeValue && safeValue.trim()),
      updatedQuestions: updatedQuestions.map(q => ({
        id: q.id,
        hasContent: Boolean(q.question && q.question.trim()),
        contentPreview: q.question ? (q.question.length > 20 ? q.question.substring(0, 20) + '...' : q.question) : 'empty'
      }))
    });
    
    setSurveyQuestions(updatedQuestions);
  };

  // Image compression function
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      // Skip compression for small files and non-jpeg/png images
      if (file.size < 100 * 1024 || // Only skip for very small files (reduced from 200KB)
          !(file.type === 'image/jpeg' || 
            file.type === 'image/png' || 
            file.type === 'image/webp')) {
        console.log('File is already small or not a compressible format, skipping compression');
        resolve(file);
        return;
      }

      console.log(`Original image size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        
        img.onload = () => {
          // Calculate new dimensions while maintaining aspect ratio
          let width = img.width;
          let height = img.height;
          
          // Determine max dimension based on file size
          let maxDimension = MAX_IMAGE_DIMENSION;
          
          // For very large images, use even smaller dimensions
          if (file.size > LARGE_IMAGE_THRESHOLD_MB * 1024 * 1024) {
            maxDimension = 250; // Even smaller for very large images
          }
          
          if (width > height && width > maxDimension) {
            height = Math.round(height * (maxDimension / width));
            width = maxDimension;
          } else if (height > maxDimension) {
            width = Math.round(width * (maxDimension / height));
            height = maxDimension;
          }
          
          console.log(`Resizing image from ${img.width}x${img.height} to ${width}x${height}`);
          
          // Create canvas for image processing
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          // Draw image on canvas
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Determine starting quality based on file size
          let startingQuality = INITIAL_QUALITY;
          
          // For larger files, start with a lower quality
          if (file.size > LARGE_IMAGE_THRESHOLD_MB * 1024 * 1024) {
            startingQuality = 0.5; // Start at lower quality for large images
          }
          
          // Compression strategy with multiple quality levels
          const compressWithQuality = (quality) => {
            // Convert to blob with specified quality
            canvas.toBlob((blob) => {
              if (!blob) {
                console.error('Canvas to Blob conversion failed');
                resolve(file); // Fall back to original file
                return;
              }
              
              console.log(`Compressed to quality ${quality.toFixed(2)}, size: ${(blob.size / 1024 / 1024).toFixed(2)}MB (${Math.round(blob.size / 1024)}KB)`);
              
              // If the result is small enough or we've reached min quality, use this result
              if (blob.size <= TARGET_SIZE_MB * 1024 * 1024 || quality <= MIN_QUALITY) {
                // Create a new file from the blob
                const compressedFile = new File([blob], file.name, {
                  type: file.type,
                  lastModified: Date.now()
                });
                
                // Log compression results
                console.log(`Compression complete. Original: ${(file.size / 1024 / 1024).toFixed(2)}MB (${Math.round(file.size / 1024)}KB), Compressed: ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB (${Math.round(compressedFile.size / 1024)}KB)`);
                console.log(`Compression ratio: ${((1 - (compressedFile.size / file.size)) * 100).toFixed(2)}%`);
                
                // If still too large at minimum quality, try more extreme measures
                if (compressedFile.size > TARGET_SIZE_MB * 1024 * 1024 && quality <= MIN_QUALITY) {
                  console.log("Image still too large at minimum quality, trying more extreme compression...");
                  
                  // Create a smaller canvas
                  const smallerCanvas = document.createElement('canvas');
                  const smallerCtx = smallerCanvas.getContext('2d');
                  
                  // Reduce dimensions further
                  const reductionFactor = Math.sqrt(TARGET_SIZE_MB * 1024 * 1024 / compressedFile.size);
                  const newWidth = Math.max(100, Math.floor(width * reductionFactor));
                  const newHeight = Math.max(100, Math.floor(height * reductionFactor));
                  
                  console.log(`Extreme compression: Resizing to ${newWidth}x${newHeight}`);
                  
                  smallerCanvas.width = newWidth;
                  smallerCanvas.height = newHeight;
                  smallerCtx.drawImage(img, 0, 0, newWidth, newHeight);
                  
                  // Force to JPEG for maximum compression
                  smallerCanvas.toBlob((finalBlob) => {
                    if (!finalBlob) {
                      console.error('Final compression failed');
                      resolve(compressedFile); // Use our best effort so far
                      return;
                    }
                    
                    const finalFile = new File([finalBlob], file.name, {
                      type: 'image/jpeg',
                      lastModified: Date.now()
                    });
                    
                    console.log(`Final compression: ${(finalFile.size / 1024).toFixed(0)}KB`);
                    resolve(finalFile);
                  }, 'image/jpeg', 0.3); // Use very low quality JPEG
                } else {
                  resolve(compressedFile);
                }
              } else {
                // Try with lower quality, faster reduction for larger files
                const qualityReduction = blob.size > 1024 * 1024 ? 0.15 : 0.1;
                const newQuality = Math.max(quality - qualityReduction, MIN_QUALITY);
                compressWithQuality(newQuality);
              }
            }, file.type, quality);
          };
          
          // Start compression with determined quality
          compressWithQuality(startingQuality);
        };
        
        img.onerror = () => {
          console.error('Failed to load image for compression');
          resolve(file); // Fall back to original file
        };
      };
      
      reader.onerror = () => {
        console.error('Failed to read file for compression');
        resolve(file); // Fall back to original file
      };
    });
  };

  const handleImageChange = (e, setError) => {
    console.log('useCampaignForm.handleImageChange called with:', {
      event: e,
      setError: typeof setError === 'function' ? 'function' : typeof setError
    });
    
    const file = e.target.files[0];
    console.log('Image file selected:', file);
    
    // Create a safe version of setError that won't crash if setError is undefined
    const safeSetError = (error) => {
      console.log('safeSetError called with:', error);
      if (typeof setError === 'function') {
        setError(error);
      } else {
        console.error('Error handler not provided:', error);
      }
    };
    
    if (!file) {
      console.log('No file selected');
      return;
    }
    
    // Check if file is an image
    if (!file.type.match('image.*')) {
      console.error('File is not an image:', file.type);
      safeSetError({
        type: 'error',
        message: 'Please select an image file (PNG, JPG, GIF, etc.)'
      });
      return;
    }
    
    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      console.error('File too large:', file.size);
      safeSetError({
        type: 'error',
        message: 'Image size should be less than 5MB'
      });
      return;
    }
    
    // First compress the image, then read and set it
    compressImage(file).then(compressedFile => {
      const reader = new FileReader();
      
      reader.onloadstart = () => {
        console.log('Starting to load compressed image');
      };
      
      reader.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          console.log(`Loading progress: ${progress}%`);
        }
      };
      
      reader.onloadend = () => {
        console.log('Compressed image loaded successfully');
        if (reader.result) {
          setPreviewImage(reader.result);
        } else {
          console.error('Failed to load image: reader.result is null');
          safeSetError({
            type: 'error',
            message: 'Failed to load image. Please try again.'
          });
        }
      };
      
      reader.onerror = (error) => {
        console.error('Error reading file:', error);
        safeSetError({
          type: 'error',
          message: 'Error reading image file. Please try again.'
        });
      };
      
      try {
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        console.error('Exception while reading file:', error);
        safeSetError({
          type: 'error',
          message: 'Error processing image file. Please try again.'
        });
      }
    }).catch(error => {
      console.error('Image compression failed:', error);
      safeSetError({
        type: 'error',
        message: 'Failed to process image. Please try again with a different image.'
      });
    });
  };

  const handleRemoveImage = () => {
    setPreviewImage(null);
  };

  const handleNext = (e) => {
    // Prevent form submission if an event is passed
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (currentStep < 5) { // 5 is the Review step (step index for a 6-step flow)
      // Regular step navigation
      setCurrentStep(currentStep + 1);
      setShowPreview(true);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      // Normal case - go back one step
      setCurrentStep(currentStep - 1);
      
      // Always show preview
      setShowPreview(true);
    }
  };

  const resetForm = () => {
    updateFormData({
      name: '',
      title: '',
      description: '',
      businessName: '',
      website: '',
      email: '',
      phone: ''
    });
    setSurveyQuestions([]);
    setPreviewImage(null);
    setCurrentStep(0);
    setAiGeneratedFields({});
  };

  return {
    formData,
    updateFormData,
    clearFormCache,
    surveyQuestions,
    setSurveyQuestions,
    aiGeneratedFields,
    setAiGeneratedFields,
    previewImage,
    setPreviewImage,
    hasExplainerVideo,
    setHasExplainerVideo,
    explainerVideo,
    setExplainerVideo,
    selectedTheme,
    setSelectedTheme,
    gradientColors,
    setGradientColors,
    gradientDirection,
    setGradientDirection,
    hexText,
    setHexText,
    currentStep,
    setCurrentStep,
    showPreview,
    setShowPreview,
    handleInputChange,
    handleAddQuestion,
    handleRemoveQuestion,
    handleQuestionChange,
    handleImageChange,
    handleRemoveImage,
    handleNext,
    handlePrevious,
    resetForm
  };
}; 